import Image from "next/image";
import React, { useRef } from "react";
import Style from "./style.module.scss";
import TechnologyTiles, {
  TechnologyTileItem,
} from "@/common/technologycard/TechnologyTiles";
import { toWebp } from "@/utils/common";

type Variant = "blue" | "green";

type TechnologyCardProps = {
  variant?: Variant; // blue or green background/border
  image: string;
  className?: string;
  heading: React.ReactNode;
  technologies: {
    id: number;
    language: string;
    image: any;
  }[];
  index: number;
  openIndex: number | null;
  setOpenIndex: (index: number | null) => void;
};

const TechnologyCard: React.FC< TechnologyCardProps> = ({ variant = "blue",
  image,
  className,
  heading,
  technologies = [],
  index,
  openIndex,
  setOpenIndex,
 }) => {
  const directionClass =
    variant === "green"
      ? "md:[direction:rtl] [direction:ltr]"
      : "md:[direction:ltr] [direction:ltr]";

  const bgImageMain =
    variant === "green"
      ? "bg-[url(/images/ServicesPage/green-bg.jpg)]"
      : "bg-[url(/images/ServicesPage/blue-bg.jpg)]";

  const cardText =
    variant === "green" ? "md:text-end text-start" : "text-start";
  const cardImage =
    variant === "green" ? "md:justify-start justify-end" : "justify-end";
  const borderColor =
    variant === "green" ? "border-green-100" : "border-border";

  const isOpen = openIndex === index;

  const handleToggle = () => {
    if (isOpen) {
      setOpenIndex(null); // close if already open
    } else {
      setOpenIndex(index); // open this one
    }
  };

  const contentRef = useRef<HTMLDivElement>(null);

  const baseUrl =
    `${process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL}` || "";

  return (
    <>
      <div
        className={`flex justify-start md:flex-row flex-col items-stretch md:gap-[30px] w-full ${directionClass} ${className}`}
      >
        {/* Left image container with background */}
        <div
          className={`shrink-0 md:block hidden ${bgImageMain} md:w-[118px] overflow-hidden xl:w-[180px] w-[81px] bg-no-repeat bg-center bg-cover flex items-center max-h-full relative`}
        >
          <Image
            src={`${baseUrl}${image}`}
            alt="Technology image"
            width={150}
            height={150}
            className="object-cover m-auto w-full absolute left-0 right-0 top-0 bottom-0 lg:max-w-[150px] md:max-w-[90px] max-w-[60px] xl:w-[150px] md:w-[90px] w-[60px]h-auto"
          />
        </div>
        <div
          className={`md:hidden flex justify-between items-center gap-[16px] pt-10 w-full ${directionClass} ${className} ${isOpen ? "pb-6" : ""}`}
        >
          <div
            className={`shrink-0 ${bgImageMain} w-[81px] bg-no-repeat overflow-hidden bg-center bg-cover flex items-center max-h-full h-[58px]  relative`}
          >
            <Image
              src={`${baseUrl}${image}`}
              alt="Technology image"
              width={150}
              height={150}
              className="object-cover m-auto absolute left-0 right-0 top-0 bottom-0 lg:max-w-[150px] md:max-w-[90px] max-w-[60px] xl:w-[150px] md:w-[90px] w-[60px] h-auto"
            />
          </div>
          <h3
            className="text-heading-5 font-bold text-background-dark grow"
            onClick={handleToggle}
          >
            {heading}
          </h3>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`origin-center transition-all duration-300 flex-[0_0_auto] ${isOpen ? "rotate-90" : "rotate-0"}`}
          >
            <path
              d="M8.91003 19.9201L15.43 13.4001C16.2 12.6301 16.2 11.3701 15.43 10.6001L8.91003 4.08008"
              stroke={isOpen ? "#0086E6" : "#32343B"}
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Text & grid */}
        <div className="w-full xl:py-2 py-0">
          <h3 className="text-heading-5 font-bold text-background-dark md:block hidden">
            {heading}
          </h3>

          {/* {(isOpen || typeof window === "undefined") && ( */}
          <div
            className={`w-full overflow-hidden xl:mt-4 lg:mt-6 md:border-b-0 md:pb-0 pb-10 border-b border-border md:max-h-[100%_!important] transition-all duration-300`}
            ref={contentRef}
            style={
              isOpen
                ? { maxHeight: contentRef.current?.scrollHeight }
                : { maxHeight: 0 }
            }
          >
            <TechnologyTiles
              context="servicesPage"
              variant={variant}
              items={technologies.map<TechnologyTileItem>((tech) => ({
                id: tech.id,
                title: tech.language,
                iconUrl: toWebp(`${baseUrl}${tech?.image?.data?.attributes?.url}`),
              }))}
              gridClassName={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 xl:gap-[31px] lg:gap-[25px_31px] gap-[21px] ${Style.mainCard} ${isOpen ? "opacity-100" : "md:opacity-100 opacity-0"}`}
              tileContainerClasses={`xl:aspect-[178_/_133] lg:aspect-[213_/_133] aspect-[178_/_133] ${borderColor} bg-white text-white w-auto flex items-center justify-center text-center border relative ${Style.mainCardInner}`}
              titleClassName={`text-subtitle-1 font-regular w-full text-secondary-text px-[15px] pt-[15px] ${cardText} ${Style.cardText}`}
              iconWrapperClassName={`absolute bottom-0 right-0 w-fit items-center justify-end px-2.5 pb-2.5 flex px-2.5 pb-2.5 w-full items-center ${cardImage} ${Style.cardImage} opacity-20`}
              innerWrapperClassName={`${Style.cardDetails}`}
              overlayExtraClassName={`${Style.bgCardImage}`}
            />
          </div>
        </div>
        {/* )} */}
      </div>
    </>
  );
};
export default TechnologyCard;
